import { BehaviorSubject, combineLatest, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, retry, switchMap, tap } from 'rxjs/operators';
import { DataSource } from '@angular/cdk/collections';
import { AbstractApiService, Filter, Pagination, Sort } from '../services/abstract-api.service';
import { ApiGetAllPaginatedResponse } from '../interfaces/api-get-all-response.interface';
import { cloneDeep, isEmpty, isEqual } from 'lodash-es';

export interface PageInfo {
    currentPage: number;
    pageCount: number;
    totalItems: number;
}

export abstract class AbstractApiDataSource<ItemType> extends DataSource<ItemType> {
    public pageInfoSubject = new BehaviorSubject<PageInfo>({
        currentPage: 1,
        pageCount: 1,
        totalItems: 0,
    });
    protected dataSubject = new Subject<ItemType[]>();
    protected cachedData: ItemType[] = [];
    protected hasSeenData = false;
    protected loadingSubject = new BehaviorSubject<boolean>(false);

    // will trigger a refresh when a new value is emitted in these subjects
    protected paginationSubject = new BehaviorSubject<Pagination | null>(null);
    protected filterSubject = new BehaviorSubject<Filter | null>({});
    protected sortSubject = new BehaviorSubject<Sort | null>(null);
    protected refreshPageSubject = new BehaviorSubject<void>(undefined);

    private initiator$: Observable<[Pagination | null, Filter | null, Sort | null, void]> = combineLatest([
        this.paginationSubject.asObservable().pipe(distinctUntilChanged(isEqual)),
        this.filterSubject.asObservable().pipe(distinctUntilChanged(isEqual)),
        this.sortSubject.asObservable().pipe(distinctUntilChanged(isEqual)),
        this.refreshPageSubject.asObservable(),
    ]);
    private dataSubscription: Subscription;
    private hasMadeAtLeastOneRequest = false;
    private hasNoData$ = this.loadingSubject.asObservable().pipe(
        distinctUntilChanged(isEqual),
        map((loading: boolean | null): boolean => {
            if (!this.hasMadeAtLeastOneRequest) {
                return false;
            }

            // can't determine if we have any data while it's still loading => assume data exists
            if (loading) {
                return false;
            }

            // can't determine whether we have (any) data if there are active filters => assume data exists
            if (!isEmpty(this.Filter)) {
                return false;
            }

            return this.cachedData.length <= 0;
        }),
    );
    private hasNoFilteredResults$ = this.loadingSubject.asObservable().pipe(
        distinctUntilChanged(isEqual),
        map((loading: boolean | null): boolean => {
            if (!this.hasMadeAtLeastOneRequest) {
                return false;
            }

            // can't determine if we have any data while it's still loading => assume data exists
            if (loading) {
                return false;
            }

            // no filter applied
            if (isEmpty(this.Filter)) {
                return false;
            }

            return this.cachedData.length <= 0;
        }),
    );

    public constructor(protected apiService: AbstractApiService<ItemType>) {
        super();
    }

    public get IsLoading$(): Observable<boolean> {
        return this.loadingSubject.asObservable();
    }

    public get PageInfo(): PageInfo {
        return this.pageInfoSubject.getValue();
    }

    public get PageInfo$(): Observable<PageInfo> {
        return this.pageInfoSubject.asObservable().pipe(distinctUntilChanged());
    }

    public get HasNoData$(): Observable<boolean> {
        return this.hasNoData$;
    }

    public get HasNoFilteredResults$(): Observable<boolean> {
        return this.hasNoFilteredResults$;
    }

    protected get Pagination(): Pagination | null {
        return this.paginationSubject.getValue();
    }

    protected set Pagination(pagination: Pagination | null) {
        this.paginationSubject.next(cloneDeep(pagination));
    }

    protected get Filter(): Filter | null {
        return this.filterSubject.value;
    }

    protected set Filter(filter: Filter | null) {
        // deep clone in order to make sure it's not passed by reference
        // (will cause distinctUntilChanged to compare the same value always)
        this.filterSubject.next(cloneDeep(filter));
    }

    protected set Sort(sort: Sort | null) {
        this.sortSubject.next(cloneDeep(sort));
    }

    public connect(): Observable<ItemType[]> {
        this.dataSubscription = this.initiator$
            .pipe(
                debounceTime(10),
                filter(([pagination]: [Pagination | null, Filter | null, Sort | null, void]) => {
                    const pageCount = this.PageInfo.pageCount;
                    // if no page provided (will defaults to page 1) or there are no pages (eg. filtered out), allow fetchPage to execute
                    if (!pagination || pageCount === 0) {
                        return true;
                    }

                    // do not allow to execute if page is 0
                    return !(pagination.page <= 0);
                }),
                tap(() => {
                    this.loadingSubject.next(true);
                }),
                // switch map will make sure to cancel the http request if a new one is being initiated before the previous ended
                switchMap((params) => {
                    return this.fetchPage(params);
                }),
            )
            .subscribe(this.dataSubject);

        return this.dataSubject;
    }

    public disconnect(): void {
        this.dataSubscription?.unsubscribe();
    }

    protected fetchPage([pagination, filter, sort]: [Pagination | null, Filter | null, Sort | null, void]): Observable<ItemType[]> {
        return this.apiService.getAllPaginated(pagination || { page: 1 }, filter || {}, sort).pipe(
            tap((response: ApiGetAllPaginatedResponse<ItemType>) => {
                // extract page data from response
                this.pageInfoSubject.next({
                    currentPage: response.page,
                    totalItems: response.totalItems,
                    pageCount: response.pageCount,
                });
            }),
            map((response: ApiGetAllPaginatedResponse<ItemType>): ItemType[] => {
                // extract the array of data from response
                this.cachedData = this.onReceiveData(response.items, pagination);
                return this.cachedData;
            }),
            // retry the request 2 times. Note: this does not sleep between requests
            // you can also retry a call from the consumer (wherever you subscribe to it) with .pipe(retry(3))
            retry(2),
            catchError(() => of([])),
            tap(() => {
                this.hasMadeAtLeastOneRequest = true;
                this.loadingSubject.next(false);
                this.hasSeenData = this.hasSeenData || this.cachedData.length > 0;
            }),
        );
    }

    // default to replace data
    protected onReceiveData(data: ItemType[], pagination: Pagination | null): ItemType[] {
        return this.replaceData(data);
    }

    protected addData(data: ItemType[]): ItemType[] {
        return this.cachedData.concat(data);
    }

    protected replaceData(data: ItemType[]): ItemType[] {
        return data;
    }

    protected replaceDataOnPage(data: ItemType[], pagination: Pagination) {
        if (!pagination.limit) {
            return this.cachedData;
        }

        return [
            ...this.cachedData.slice(0, (pagination.page - 1) * pagination.limit),
            ...data,
            ...this.cachedData.slice(pagination.page * pagination.limit),
        ];
    }
}
